import { Controller } from "@hotwired/stimulus"

// Mounts wherever payments_helper.rb#payment_method_component renders: billings, account edit,
// subscriptions, custom packagings, and both owner sections of the prepayments page.
//
// Two elements stay page-global lookups rather than targets: #payment-method-delete-modal, shared
// across every mount on a page, and #complete-payment, which only the prepayments page renders.
//
// newUserCard never matches anything - the "user" form id it names renders on no view - while
// newOrganizationCard is real.
//
// The synthetic click on .current-payment-method after a successful add must stay jQuery-triggered:
// give_payment_controller.js, custom_packaging_payment_controller.js and prepayment_controller.js
// all hold jQuery-delegated listeners on it. Replacing it with a direct call or a CustomEvent would
// silently strip auto-select from every page that composes a payment method picker.
export default class extends Controller {
  static get targets() {
    return [
      "cardNumber", "cardExp", "newCardButton", "newOrganizationCard", "newUserCard",
      "stripeCreditCardForm", "creditCardForm", "currentPaymentMenuButton", "noPaymentMethodsTile"
    ];
  }

  connect() {
    this.bindPlugins();
  }

  // Mirrors jQuery 3's own show/hide: it restores an explicit inline value rather than clearing to
  // "" unconditionally (patterns.md #11). Every element these three touch has no competing
  // non-!important `display` rule in the stylesheets (checked against payment.scss,
  // payment_methods.scss, dropdowns.scss, forms.scss, searchbar.scss), so "" is safe to un-hide.
  showEl(el) {
    if (el) { el.style.display = ""; }
  }

  hideEl(el) {
    if (el) { el.style.display = "none"; }
  }

  toggleEl(el) {
    if (el) { el.style.display = el.style.display === "none" ? "" : "none"; }
  }

  // Re-invoked after the swapped card fragment lands: Stimulus rebinds data-action targets in
  // inserted markup on its own, but jquery.payment/validateCreditCard are imperative plugin calls
  // that have to be pointed at the new node explicitly.
  bindPlugins() {
    if (this.hasCardNumberTarget) {
      // jquery.payment plugin call (formatCardNumber/formatCardExpiry), so it stays on jQuery.
      $(this.cardNumberTarget).payment("formatCardNumber");

      const validCardTypes = ["visa", "mastercard", "amex", "discover"];
      // validateCreditCard is a plugin call too (jquery.payment/creditCardValidator family) -- same
      // allowlist gap as above, stays on jQuery.
      $(this.cardNumberTarget).validateCreditCard((result) => {
        const inputAddonIcon = this.cardNumberTarget.closest(".input-addon-group")?.querySelector(".input-addon i");

        if (result.card_type !== null && validCardTypes.includes(result.card_type.name)) {
          document.getElementById(`${result.card_type.name}-logo`)?.classList.remove("hidden");
          this.hideEl(inputAddonIcon);
        } else {
          document.querySelectorAll(".card-validator").forEach((el) => el.classList.add("hidden"));
          this.showEl(inputAddonIcon);
        }

        if (result.valid) {
          this.cardNumberTarget.classList.add("is-valid");
        } else {
          this.cardNumberTarget.classList.remove("is-valid");
        }
      });
    }

    if (this.hasCardExpTarget) {
      $(this.cardExpTarget).payment("formatCardExpiry");
    }
  }

  // formEl is a native element here -- callers pass a Target directly. Left on jQuery in
  // tokenizeCard/stripeCardResponseHandler is the $form/$creditCardForm/$stripeForm cluster, which
  // stays jQuery-wrapped for .serialize()/$.ajax (see the comment on stripeCardResponseHandler); this
  // pair doesn't touch that chain, so it takes plain elements.
  enableButtons(formEl) {
    const button = formEl.querySelector(".add-new-card");
    if (button) { button.disabled = false; }
  }

  disableButtons(formEl) {
    const button = formEl.querySelector(".add-new-card");
    if (button) { button.disabled = true; }
  }

  addOrReplaceHiddenInput(formId, formModel, newId) {
    if (!formId || !formModel) { return; }

    const $existingInput = $(`input#${formModel}_payment_method_id`);
    if ($existingInput.length > 0) {
      $existingInput.val(newId).trigger("change");
    } else {
      $(`#${formId}`).append(`<input type='hidden' name='${formModel}[payment_method_id]' id='${formModel}_payment_method_id' value='${newId}' />`);
      $(`#${formModel}_payment_method_id`).trigger("change");
    }
  }

  // Sends cc data to Stripe for tokenization -- the form's own submit, a genuine DOM event, so
  // this is a plain data-action rather than a jQuery binding (Step 6c).
  tokenizeCard(event) {
    event.preventDefault();
    const $form = $(this.stripeCreditCardFormTarget);

    if ($form.find("input[data-stripe=name]").val().length === 0) {
      $form.find(".payment-errors-container").removeClass("hidden");
      $form.find(".payment-errors").html("Name must be populated");
      return;
    }

    $form.find(".loader").removeClass("hidden");
    this.disableButtons(this.stripeCreditCardFormTarget);

    const expiration = $form.find(".card-exp").payment("cardExpiryVal");

    Stripe.card.createToken({
      name: $form.find(".card-name").val(),
      number: $form.find(".card-number").val(),
      cvc: $form.find(".card-cvc").val(),
      exp_month: (expiration.month || 0),
      exp_year: (expiration.year || 0)
    }, (status, response) => this.stripeCardResponseHandler(status, response));
  }

  // Captures the Stripe response and posts it to create. Stays on $.ajax rather than fetch, which
  // saves translating the headers Rails needs - jQuery already sets them.
  stripeCardResponseHandler(status, response) {
    const $stripeForm = $(this.stripeCreditCardFormTarget);
    const $creditCardForm = $(this.creditCardFormTarget);

    if (response.error) {
      $stripeForm.find(".payment-errors-container").removeClass("hidden");
      $stripeForm.find(".payment-errors").text(response.error.message);
      this.enableButtons(this.stripeCreditCardFormTarget);
      $stripeForm.find(".loader").addClass("hidden");
      return;
    }

    const token = response.id;
    const last4 = response.card.last4;
    const name = response.card.name;
    $creditCardForm.find("#payment_method_stripe_token").val(token);
    $creditCardForm.find("#payment_method_stripe_last4").val(last4);
    $creditCardForm.find("#payment_method_stripe_name").val(name);

    this.disableButtons(this.creditCardFormTarget);
    const formId = $creditCardForm.data("form");
    const formModel = $creditCardForm.data("model");

    $.ajax({
      type: "POST",
      url: $creditCardForm.attr("action"),
      data: $creditCardForm.serialize(),
      dataType: "JSON"
    }).fail((xhr, textStatus) => {
      $stripeForm.find(".payment-errors-container").removeClass("hidden");
      $stripeForm.find(".payment-errors").text(xhr.responseJSON.message);
      this.enableButtons(this.stripeCreditCardFormTarget);
      $stripeForm.find(".loader").addClass("hidden");
      console.warn("Request failed: " + textStatus);
    }).done((data) => {
      this.addOrReplaceHiddenInput(formId, formModel, data.id);
      // this.element survives the swap, so Stimulus rebinds the data-actions itself; only the
      // plugin calls need the manual bindPlugins() re-run below.
      $creditCardForm.closest(".payment-method-component").replaceWith(data.html);

      if (data.autoselect_payment_method) {
        $(".current-payment-method").click();
      }

      // R4/D14 standing skip: completePaymentButton is a page-global lookup (#complete-payment),
      // never this controller's own mount for any view, so it can't take the Classes API.
      const completePaymentButton = document.getElementById("complete-payment");
      if (completePaymentButton) { completePaymentButton.classList.remove("hidden"); }

      window.dataLayer = window.dataLayer || [];
      if (data.ga4_events) { window.dataLayer.push(...data.ga4_events); }

      this.bindPlugins();
    }).always(() => {
      this.enableButtons(this.creditCardFormTarget);
    });
  }

  replaceCard(event) {
    event.preventDefault();
    const widget = event.currentTarget.closest(".payment-method-widget");
    if (widget) {
      Array.from(widget.parentElement.children)
        .filter((el) => el !== widget && el.classList.contains("payment-method-widget"))
        .forEach((el) => this.toggleEl(el));
    }

    if (this.hasCurrentPaymentMenuButtonTarget) { this.hideEl(this.currentPaymentMenuButtonTarget); }
    event.currentTarget.closest(".picklist")?.classList.remove("open");
  }

  toggleNewCard(event) {
    event.preventDefault();
    if (this.hasNewOrganizationCardTarget) { this.toggleEl(this.newOrganizationCardTarget); }
    if (this.hasNewUserCardTarget) { this.toggleEl(this.newUserCardTarget); }

    if (this.hasNoPaymentMethodsTileTarget) {
      this.toggleEl(this.noPaymentMethodsTileTarget);
    } else if (this.hasNewCardButtonTarget) {
      this.toggleEl(this.newCardButtonTarget);
    }
  }

  openDeleteModal(event) {
    event.preventDefault();
    const deleteModal = document.getElementById("payment-method-delete-modal");
    // Bootstrap modal plugin call, stays on jQuery (D7).
    $(deleteModal).modal("show");
    deleteModal?.querySelector(".modal-footer .btn[data-method='delete']")
      ?.setAttribute("href", event.currentTarget.getAttribute("href"));
  }

  cancelNewCard(event) {
    event.preventDefault();
    if (this.hasNewOrganizationCardTarget) { this.toggleEl(this.newOrganizationCardTarget); }
    if (this.hasNewUserCardTarget) { this.toggleEl(this.newUserCardTarget); }
    if (this.hasCurrentPaymentMenuButtonTarget) { this.showEl(this.currentPaymentMenuButtonTarget); }

    if (this.hasNoPaymentMethodsTileTarget) {
      this.toggleEl(this.noPaymentMethodsTileTarget);
    } else if (this.hasNewCardButtonTarget) {
      this.toggleEl(this.newCardButtonTarget);
    }
  }
}
